import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { I } from "../../Components/CMI/icons";
import { Card, Badge } from "../../Components/CMI/primitives";
import PageHead, { Footer } from "../../Layouts/CMI/PageHead";
import { getPresupuesto, savePresupuestoDetalle } from "../../helpers/configApi";

const ESTADO_BADGE = { borrador: "warn", aprobado: "pos", cerrado: "neutral" };

const fmt = (n) => {
  const v = parseFloat(n) || 0;
  if (v === 0) return "—";
  return v.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const fmtTotal = (n) => {
  const v = parseFloat(n) || 0;
  return v.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

function buildTree(detalle) {
  const cuentaMap = {};
  for (const d of detalle) {
    const area = d.area || "SIN CLASIFICAR";
    const key = d.numero_cuenta;
    if (!cuentaMap[key]) {
      cuentaMap[key] = {
        numero_cuenta: d.numero_cuenta,
        denominacion: d.denominacion || d.numero_cuenta,
        area,
        categoria: d.categoria_pyg || "Sin categoría",
        subcategoria: d.subcategoria_pyg || "Sin subcategoría",
        importe: parseFloat(d.importe) || 0,
      };
    }
  }

  const cuentas = Object.values(cuentaMap);
  const tree = {};
  for (const c of cuentas) {
    if (!tree[c.area]) tree[c.area] = {};
    if (!tree[c.area][c.categoria]) tree[c.area][c.categoria] = {};
    if (!tree[c.area][c.categoria][c.subcategoria]) tree[c.area][c.categoria][c.subcategoria] = [];
    tree[c.area][c.categoria][c.subcategoria].push(c);
  }

  return { tree, cuentas };
}

function sumImportes(items) {
  return items.reduce((t, c) => t + c.importe, 0);
}

const AREA_COLORS = {
  INGRESOS: "#10B981", GASTOS: "#EF4444", PRODUCCION: "#2A5BFF",
  VENTAS: "#0AB4D4", MARKETING: "#EC4899", SISTEMAS: "#8B5CF6",
  "ADMINISTRACION Y FINANZAS": "#F59E0B", "OTROS CORPORATE SUPPORT": "#6B7280",
  "DIRECCION GENERAL": "#14B8A6", "SIN CLASIFICAR": "#9CA3AF",
};

const HeaderRow = ({ label, total, color, level, collapsed, onToggle }) => {
  const padLeft = level === 0 ? 0 : level === 1 ? 16 : 32;
  const fontSize = level === 0 ? 13 : 12;
  const fontWeight = level <= 1 ? 700 : 600;
  const bg = level === 0 ? "var(--ce-bg-2)" : level === 1 ? "rgba(0,0,0,0.02)" : "transparent";

  return (
    <tr style={{ background: bg }}>
      <td style={{ ...cellStyle, paddingLeft: 10 + padLeft, fontWeight, fontSize, color: color || "var(--ce-fg)", whiteSpace: "nowrap" }}>
        <span style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }} onClick={onToggle}>
          {level <= 1 && <span style={{ fontSize: 10, color: "var(--ce-fg-muted)", transition: "transform 120ms", transform: collapsed ? "rotate(-90deg)" : "rotate(0)" }}>▼</span>}
          {level === 0 && <span style={{ width: 8, height: 8, borderRadius: 4, background: color, flexShrink: 0 }} />}
          {label}
        </span>
      </td>
      <td style={{ ...numCellStyle, fontWeight, fontSize, color: color || "var(--ce-fg)" }}>
        {fmtTotal(total)}
      </td>
      <td style={{ ...numCellStyle, fontWeight, fontSize, color: color || "var(--ce-fg)", background: "rgba(0,0,0,0.02)" }}>
        {fmtTotal(total * 12)}
      </td>
    </tr>
  );
};

const CuentaRow = ({ cuenta, editing, editValue, onEdit, onSave, onCancel, onChange }) => {
  const importe = editing ? (parseFloat(editValue) || 0) : cuenta.importe;

  return (
    <tr>
      <td style={{ ...cellStyle, paddingLeft: 58, fontSize: 12, color: "var(--ce-fg-2)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 400 }}
        title={`${cuenta.numero_cuenta} — ${cuenta.denominacion}`}>
        <span style={{ fontFamily: "var(--ce-font-mono)", fontSize: 10, color: "var(--ce-fg-muted)", marginRight: 6 }}>{cuenta.numero_cuenta}</span>
        {cuenta.denominacion}
        {!editing && (
          <button onClick={onEdit} style={{ background: "none", border: 0, cursor: "pointer", color: "var(--ce-fg-faint)", padding: "0 4px", marginLeft: 4, opacity: 0.5 }} className="row-edit-btn">
            <I.Pencil size={11} />
          </button>
        )}
      </td>
      <td style={numCellStyle}>
        {editing ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4 }}>
            <input
              type="number"
              step="0.01"
              value={editValue}
              onChange={e => onChange(e.target.value)}
              autoFocus
              onKeyDown={e => { if (e.key === "Enter") onSave(); if (e.key === "Escape") onCancel(); }}
              style={{
                width: 100, padding: "3px 6px", textAlign: "right", fontSize: 12,
                border: "1px solid var(--ce-primary)", borderRadius: 4,
                background: "var(--ce-bg)", color: "var(--ce-fg)",
                fontFamily: "var(--ce-font-mono)",
              }}
            />
            <button onClick={onSave} style={{ background: "none", border: 0, cursor: "pointer", color: "var(--ce-success)", padding: 2 }}><I.Check size={14} /></button>
            <button onClick={onCancel} style={{ background: "none", border: 0, cursor: "pointer", color: "var(--ce-fg-muted)", padding: 2 }}><I.X size={14} /></button>
          </div>
        ) : (
          <span style={{ color: importe === 0 ? "var(--ce-fg-faint)" : "var(--ce-fg)" }}>{fmt(importe)}</span>
        )}
      </td>
      <td style={{ ...numCellStyle, background: "rgba(0,0,0,0.02)", color: importe === 0 ? "var(--ce-fg-faint)" : "var(--ce-fg)" }}>
        {fmt(importe * 12)}
      </td>
    </tr>
  );
};

const PresupuestoEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingCuenta, setEditingCuenta] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [collapsed, setCollapsed] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getPresupuesto(id);
      setData(result);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <>
      <PageHead icon={I.Coin} title="Presupuesto" sub="Cargando…" />
      <div style={{ textAlign: "center", padding: 60, color: "var(--ce-fg-muted)" }}>Cargando presupuesto…</div>
    </>
  );

  if (!data) return (
    <>
      <PageHead icon={I.Coin} title="Presupuesto" sub="No encontrado" />
      <Card><div style={{ padding: 40, textAlign: "center", color: "var(--ce-fg-muted)" }}>Presupuesto no encontrado.</div></Card>
    </>
  );

  const { tree, cuentas } = buildTree(data.detalle || []);
  const AREA_ORDER = ["INGRESOS", "PRODUCCION", "VENTAS", "MARKETING", "SISTEMAS", "ADMINISTRACION Y FINANZAS", "DIRECCION GENERAL", "OTROS CORPORATE SUPPORT", "SIN CLASIFICAR"];
  const areas = Object.keys(tree).sort((a, b) => {
    const ia = AREA_ORDER.indexOf(a), ib = AREA_ORDER.indexOf(b);
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
  });
  const totalIngresos = sumImportes(cuentas.filter(c => c.area === "INGRESOS"));
  const totalGastos = sumImportes(cuentas.filter(c => c.area !== "INGRESOS"));
  const resultado = totalIngresos - totalGastos;

  const toggleCollapse = (key) => setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));

  const startEdit = (cuenta) => {
    setEditingCuenta(cuenta.numero_cuenta);
    setEditValue(String(cuenta.importe || 0));
  };

  const cancelEdit = () => { setEditingCuenta(null); setEditValue(""); };

  const handleSave = async (cuenta) => {
    setSaving(true);
    try {
      const newImporte = parseFloat(editValue) || 0;
      const allDetalle = cuentas.map(c => ({
        numero_cuenta: c.numero_cuenta,
        importe: c.numero_cuenta === cuenta.numero_cuenta ? newImporte : c.importe,
      })).filter(d => d.importe !== 0);
      await savePresupuestoDetalle(id, allDetalle);
      setEditingCuenta(null);
      setEditValue("");
      await load();
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const fechaInicioFmt = data.fecha_inicio
    ? new Date(data.fecha_inicio).toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" })
    : "";

  return (
    <>
      <PageHead
        icon={I.Coin}
        title={data.nombre || "Presupuesto"}
        sub={`${data.ejercicio} · v${data.version}${fechaInicioFmt ? ` · Vigente desde ${fechaInicioFmt}` : ""}`}
        actions={
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <Badge tone={ESTADO_BADGE[data.estado] || "neutral"}>{data.estado}</Badge>
            <button onClick={() => navigate("/dashboard-config?section=presup")}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 16px", borderRadius: 8, border: "1px solid var(--ce-border)", background: "var(--ce-bg)", color: "var(--ce-fg)", cursor: "pointer", fontSize: 13 }}>
              <I.ArrowRight size={14} style={{ transform: "rotate(180deg)" }} /> Volver
            </button>
          </div>
        }
      />

      <Card>
        <div style={{ overflow: "auto", maxHeight: "calc(100vh - 200px)" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: "var(--ce-bg-2)", position: "sticky", top: 0, zIndex: 3 }}>
                <th style={{ ...headerCellStyle, minWidth: 360 }}>Cuenta</th>
                <th style={{ ...headerCellStyle, minWidth: 120, textAlign: "right" }}>Importe mensual</th>
                <th style={{ ...headerCellStyle, minWidth: 120, textAlign: "right", background: "rgba(0,0,0,0.04)" }}>Anualizado</th>
              </tr>
            </thead>
            <tbody>
              {areas.map(area => {
                const areaColor = AREA_COLORS[area] || "var(--ce-fg)";
                const areaItems = [];
                for (const cat of Object.keys(tree[area])) {
                  for (const sub of Object.keys(tree[area][cat])) {
                    areaItems.push(...tree[area][cat][sub]);
                  }
                }
                const areaTotal = sumImportes(areaItems);
                const areaKey = `area:${area}`;
                const areaCollapsed = collapsed[areaKey];

                return (
                  <React.Fragment key={area}>
                    <HeaderRow label={area} total={areaTotal} color={areaColor} level={0} collapsed={areaCollapsed} onToggle={() => toggleCollapse(areaKey)} />
                    {!areaCollapsed && Object.keys(tree[area]).map(cat => {
                      const catItems = [];
                      for (const sub of Object.keys(tree[area][cat])) {
                        catItems.push(...tree[area][cat][sub]);
                      }
                      const catTotal = sumImportes(catItems);
                      const catKey = `cat:${area}:${cat}`;
                      const catCollapsed = collapsed[catKey];

                      return (
                        <React.Fragment key={cat}>
                          <HeaderRow label={cat} total={catTotal} level={1} collapsed={catCollapsed} onToggle={() => toggleCollapse(catKey)} />
                          {!catCollapsed && Object.keys(tree[area][cat]).map(sub => {
                            const subItems = tree[area][cat][sub];
                            const showSubHeader = sub !== cat;
                            const subTotal = sumImportes(subItems);

                            return (
                              <React.Fragment key={sub}>
                                {showSubHeader && <HeaderRow label={sub} total={subTotal} level={2} onToggle={() => {}} />}
                                {subItems.map(cuenta => (
                                  <CuentaRow
                                    key={cuenta.numero_cuenta}
                                    cuenta={cuenta}
                                    editing={editingCuenta === cuenta.numero_cuenta}
                                    editValue={editingCuenta === cuenta.numero_cuenta ? editValue : null}
                                    onEdit={() => startEdit(cuenta)}
                                    onSave={() => handleSave(cuenta)}
                                    onCancel={cancelEdit}
                                    onChange={setEditValue}
                                  />
                                ))}
                              </React.Fragment>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </React.Fragment>
                );
              })}

            </tbody>
            <tfoot>
              <tr style={{ background: "var(--ce-bg-2)", borderTop: "2px solid var(--ce-border)" }}>
                <td style={{ ...cellStyle, fontWeight: 700, fontSize: 13, background: "var(--ce-bg-2)", color: "#10B981" }}>TOTAL INGRESOS</td>
                <td style={{ ...numCellStyle, fontWeight: 700, fontSize: 13, background: "var(--ce-bg-2)", color: "#10B981" }}>{fmtTotal(totalIngresos)}</td>
                <td style={{ ...numCellStyle, fontWeight: 700, fontSize: 13, background: "var(--ce-bg-2)", color: "#10B981" }}>{fmtTotal(totalIngresos * 12)}</td>
              </tr>
              <tr style={{ background: "var(--ce-bg-2)" }}>
                <td style={{ ...cellStyle, fontWeight: 700, fontSize: 13, background: "var(--ce-bg-2)", color: "#EF4444" }}>TOTAL GASTOS</td>
                <td style={{ ...numCellStyle, fontWeight: 700, fontSize: 13, background: "var(--ce-bg-2)", color: "#EF4444" }}>{fmtTotal(totalGastos)}</td>
                <td style={{ ...numCellStyle, fontWeight: 700, fontSize: 13, background: "var(--ce-bg-2)", color: "#EF4444" }}>{fmtTotal(totalGastos * 12)}</td>
              </tr>
              <tr style={{ background: "var(--ce-bg-2)", position: "sticky", bottom: 0, zIndex: 3 }}>
                <td style={{ ...cellStyle, fontWeight: 700, fontSize: 13, background: "var(--ce-bg-2)" }}>RESULTADO</td>
                <td style={{ ...numCellStyle, fontWeight: 700, fontSize: 13, background: "var(--ce-bg-2)", color: resultado >= 0 ? "#10B981" : "#EF4444" }}>{fmtTotal(resultado)}</td>
                <td style={{ ...numCellStyle, fontWeight: 700, fontSize: 13, background: "var(--ce-bg-2)", color: resultado >= 0 ? "#10B981" : "#EF4444" }}>{fmtTotal(resultado * 12)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </Card>

      {saving && <div style={{ position: "fixed", bottom: 20, right: 20, background: "var(--ce-primary)", color: "#fff", padding: "10px 20px", borderRadius: 8, fontSize: 13, fontWeight: 600, zIndex: 999 }}>Guardando…</div>}

      <style>{`tr:hover .row-edit-btn { opacity: 1 !important; }`}</style>
      <Footer />
    </>
  );
};

const headerCellStyle = {
  padding: "8px 10px", textAlign: "left", fontSize: 11, fontWeight: 600,
  color: "var(--ce-fg-muted)", borderBottom: "1px solid var(--ce-border)",
};
const cellStyle = { padding: "6px 10px", borderBottom: "1px solid var(--ce-border-subtle)" };
const numCellStyle = { padding: "6px 8px", textAlign: "right", fontSize: 12, fontFamily: "var(--ce-font-mono)", borderBottom: "1px solid var(--ce-border-subtle)", color: "var(--ce-fg)" };

export default PresupuestoEditor;
